import { useState, useMemo, useContext, type RefObject } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import "./Table.css";
import { PickupLocationContext } from "../store/PickupLocationContext";
import type { Delivery } from "../utils/types";
import { DeliveryContext } from "../store/DeliveryContext";
import { formatPhoneNumber } from "../utils/PhoneFormatters";
import { SubscriptionsContext } from "../store/SubscriptionsContext";
import ScrollableContainer from "./ScrollableContainer";
import LoadingSpinner from "../../LoadingSpinner";

type DeliverySortKeys = Exclude<keyof Delivery, "id" | "friendPickup">;

type DeliveryTableProps = {
  printableRef: RefObject<HTMLTableElement | null>;
};

const DeliveryTable = ({ printableRef }: DeliveryTableProps) => {
  const { tiers, subscriptions } = useContext(SubscriptionsContext);
  const { pickupLocations } = useContext(PickupLocationContext);
  const { deliveries, loading } = useContext(DeliveryContext);

  const [sortKey, setSortKey] = useState<DeliverySortKeys | "">("");
  const [filteredLocation, setFilteredLocation] = useState("");
  const [filteredDay, setFilteredDay] = useState("");
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const activeFilterSummary = useMemo(() => {
    const parts: string[] = [];
    if (sortKey === "pickupLocation") parts.push("Sort: Pickup Location");
    if (sortKey === "day") parts.push("Sort: Day");
    if (filteredLocation) parts.push(`Location: ${filteredLocation}`);
    if (filteredDay) parts.push(`Day: ${filteredDay}`);
    return parts.length > 0 ? parts.join(" · ") : "";
  }, [sortKey, filteredLocation, filteredDay]);

  //PRINTING

  //FILTERING & SORTING
  const deliveriesToDisplay = useMemo(() => {
    let filteredDeliveries = deliveries;

    if (filteredLocation && filteredDay.length === 0) {
      filteredDeliveries = deliveries.filter(
        (delivery) => delivery.pickupLocation === filteredLocation,
      );
    }

    if (filteredDay && filteredLocation.length === 0) {
      filteredDeliveries = deliveries.filter((delivery) => delivery.day === filteredDay);
    }

    if (filteredDay.length > 0 && filteredLocation.length > 0) {
      filteredDeliveries = deliveries.filter(
        (delivery) =>
          delivery.pickupLocation === filteredLocation &&
          delivery.day === filteredDay,
      );
    }

    const byName = (a: Delivery, b: Delivery) =>
      a.firstName.localeCompare(b.firstName) ||
      a.surname.localeCompare(b.surname);

    if (!sortKey) return [...filteredDeliveries].sort(byName);

    return [...filteredDeliveries].sort((a, b) => {
      const valA = a[sortKey].toString().toLowerCase();
      const valB = b[sortKey].toString().toLowerCase();
      if (valA < valB) return -1;
      if (valA > valB) return 1;
      return byName(a, b);
    });
  }, [deliveries, filteredLocation, sortKey, filteredDay]);

  const availableDays = pickupLocations.flatMap((location) => {
    return location.days;
  });

  function getUniqueDays() {
    const uniqueDays: string[] = [];

    if (filteredLocation) {
      const filteredLocationObj = pickupLocations.find((pickupLocation) => {
        return pickupLocation.name === filteredLocation;
      });

      if (filteredLocationObj?.days) {
        for (const availableDay of filteredLocationObj.days) {
          uniqueDays.push(availableDay.day);
        }
        return uniqueDays;
      }
    }

    for (const availableDay of availableDays) {
      if (!uniqueDays.includes(availableDay.day)) {
        uniqueDays.push(availableDay.day);
      }
    }
    return uniqueDays;
  }

  const pickupLocationOptions = pickupLocations.map((location) => {
    return (
      <option key={location._id} value={location.name}>
        {location.name}
      </option>
    );
  });
  // const xx = filteredLocation
  const pickupDayOptions = getUniqueDays().map((uniqueDay) => {
    return (
      <option key={uniqueDay} value={uniqueDay}>
        {uniqueDay}
      </option>
    );
  });

  const subscriptionTotals = useMemo(() => {
    const tierToSubscriptionId = new Map(
      tiers.map((tier) => [tier.id, tier.subscriptionId]),
    );
    const subscriptionIdToName = new Map(
      subscriptions.map((subscription) => [subscription.id, subscription.name]),
    );
    const totalsByName = new Map<string, number>();

    for (const delivery of deliveriesToDisplay) {
      const subscriptionId = tierToSubscriptionId.get(delivery.subscriptionTierId);
      const subscriptionName = subscriptionId
        ? subscriptionIdToName.get(subscriptionId) ?? "Unknown subscription"
        : "Unknown subscription";

      totalsByName.set(
        subscriptionName,
        (totalsByName.get(subscriptionName) ?? 0) + 1,
      );
    }

    return Array.from(totalsByName.entries())
      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
      .map(([name, count]) => ({ name, count }));
  }, [deliveriesToDisplay, tiers, subscriptions]);

  //TOTAL
  let totalCount;

  if (filteredLocation === "" && filteredDay === "") {
    totalCount = `Total deliveries this week: ${deliveries.length}`;
  }
  if (filteredLocation !== "" && filteredDay === "") {
    totalCount = `Total deliveries to ${filteredLocation}: ${deliveriesToDisplay.length}`;
  }
  if (filteredLocation === "" && filteredDay !== "") {
    totalCount = `Total deliveries on ${filteredDay}: ${deliveriesToDisplay.length}`;
  }
  if (filteredLocation !== "" && filteredDay !== "") {
    totalCount = `Total deliveries to ${filteredLocation} on ${filteredDay}: ${deliveriesToDisplay.length}`;
  }

  return (
    <>
      <div className="deliveries-heading">
        <div
          className={
            filtersExpanded
              ? "sort-filter-table info-card info-card-100"
              : "sort-filter-table info-card info-card-100 sort-filter-table--collapsed"
          }
        >
          <button
            type="button"
            className="delivery-filters-toggle"
            onClick={() => setFiltersExpanded((open) => !open)}
            aria-expanded={filtersExpanded}
            aria-controls="delivery-filters-panel"
          >
            <span className="delivery-filters-toggle-text">
              <span className="delivery-filters-toggle-title">
                Sort &amp; filter
              </span>
              {!filtersExpanded && activeFilterSummary && (
                <span className="delivery-filters-summary">
                  {activeFilterSummary}
                </span>
              )}
            </span>
            <FontAwesomeIcon
              icon={filtersExpanded ? faChevronUp : faChevronDown}
              className="delivery-filters-chevron"
              aria-hidden
            />
          </button>
          <div
            id="delivery-filters-panel"
            className={
              filtersExpanded
                ? "delivery-filters-panel delivery-filters-panel--open"
                : "delivery-filters-panel"
            }
          >
            <div className="delivery-filters-panel-inner">
              <div className="filters">
                <label htmlFor="sort" className="sort-label">
                  Sort by:
                </label>
                <select
                  className="dropdown sort-dropdown"
                  id="sort"
                  value={sortKey}
                  onChange={(e) =>
                    setSortKey(e.target.value as DeliverySortKeys)
                  }
                >
                  <option value="">-- None --</option>
                  <option value="pickupLocation">Pickup Location</option>
                  <option value="day">Day</option>
                </select>
              </div>
              <div className="filters">
                <label htmlFor="location" className="sort-label">
                  Filter by location:
                </label>
                <select
                  className="dropdown sort-dropdown"
                  id="location"
                  value={filteredLocation}
                  onChange={(e) => setFilteredLocation(e.target.value)}
                >
                  <option value="">All Locations</option>
                  {pickupLocationOptions}
                </select>
              </div>
              <div className="filters">
                <label htmlFor="day" className="sort-label">
                  Filter by Day:
                </label>
                <select
                  className="dropdown sort-dropdown"
                  id="day"
                  value={filteredDay}
                  onChange={(e) => setFilteredDay(e.target.value)}
                >
                  <option value="">All Days</option>
                  {pickupDayOptions}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="deliveries-container">
          <ScrollableContainer>
            <div ref={printableRef} className="print">
              <table className="table">
                <thead className="sticky-table-head">
                  <tr>
                    <th className="th">Name</th>
                    <th className="th">Phone</th>
                    <th className="th">Pickup Location</th>
                    <th className="th">Day</th>
                    <th className="th">Subscription</th>
                    <th className="th">Friend Pickup</th>
                    <th className="th">Friend Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveriesToDisplay.map((delivery) => {
                    const tierOnDelivery = tiers.find(
                      (tierOption) =>
                        tierOption.id === delivery.subscriptionTierId,
                    );
                    const subscriptionObject = subscriptions.find(
                      (sub) => sub.id === tierOnDelivery?.subscriptionId,
                    );
                    const subscriptionName = subscriptionObject?.name;

                    return (
                      <tr key={delivery.id}>
                        <td className="td capitalized">
                          {delivery.subscriptionPaymentStatus === "paid" ? (
                            delivery.firstName + " " + delivery.surname
                          ) : (
                            <span className="unpaid-tag">
                              {delivery.firstName + " " + delivery.surname}
                            </span>
                          )}
                        </td>
                        <td className="td">
                          {formatPhoneNumber(delivery.phoneNumber)}
                        </td>
                        <td className="td capitalized">
                          {delivery.pickupLocation}
                        </td>
                        <td className="td capitalized">{delivery.day}</td>
                        <td className="td">{subscriptionName}</td>
                        <td className="td capitalized">
                          {delivery.friendPickup?.firstName}{" "}
                          {delivery.friendPickup?.surname}
                        </td>
                        <td className="td">
                          {formatPhoneNumber(
                            delivery.friendPickup?.phoneNumber,
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {subscriptionTotals.map((subscriptionTotal) => (
                    <tr key={subscriptionTotal.name}>
                      <td className="td td-left" colSpan={7}>
                        {`${subscriptionTotal.name}: ${subscriptionTotal.count}`}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className="td td-left" colSpan={7}>
                      {totalCount}
                    </td>
                  </tr>
                </tfoot>
              </table>
              <table className="table print-footer">
                <tfoot>
                  {subscriptionTotals.map((subscriptionTotal) => (
                    <tr key={`print-${subscriptionTotal.name}`}>
                      <td className="td td-left" colSpan={7}>
                        {`${subscriptionTotal.name}: ${subscriptionTotal.count}`}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className="td td-left" colSpan={7}>
                      {totalCount}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </ScrollableContainer>
        </div>
      )}
    </>
  );
};

export default DeliveryTable;
